import { useState, useEffect, useRef } from "react";
import { Customer } from "@/api.types";
import { useCustomers } from "@/api/customers";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useFormContext } from "react-hook-form";

// Custom hook for debouncing values
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface CustomerSearchProps {
  value?: string | null;
  onValueChange: (value: string) => void;
  onCustomerSelect?: (customer: Customer | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function CustomerSearch({
  value,
  onValueChange,
  onCustomerSelect,
  placeholder = "Select customer...",
  className,
  disabled = false,
}: CustomerSearchProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  // Fetch customers with pagination and search
  const { data: response, isFetching: isLoading } = useCustomers({
    limit: 20,
    page,
    search: debouncedSearchTerm,
  });

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  // Update allCustomers when new data is fetched
  useEffect(() => {
    if (response?.data) {
      if (page === 1) {
        // Replace existing data if we're on page 1 (new search)
        setAllCustomers(response.data);
      } else {
        // Append data if we're loading more pages
        setAllCustomers((prev) => [...prev, ...response.data]);
      }
    }
  }, [response?.data, page]);

  // Handle scrolling to load more
  const handleScroll = () => {
    if (!listRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const hasReachedBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (
      hasReachedBottom &&
      !isLoadingMore &&
      response?.pagination?.hasNextPage
    ) {
      setIsLoadingMore(true);
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Reset loading more state when fetching completes
  useEffect(() => {
    if (!isLoading) {
      setIsLoadingMore(false);
    }
  }, [isLoading]);

  // Find the selected customer for display
  const selectedCustomer = allCustomers.find(
    (customer) => customer.id === value
  );

  // Call onCustomerSelect when selectedCustomer changes
  useEffect(() => {
    if (onCustomerSelect) {
      onCustomerSelect(selectedCustomer);
    }
  }, [selectedCustomer, onCustomerSelect]);

  // Display text for the selected customer
  const getSelectedCustomerText = () => {
    if (!value) return placeholder;

    if (selectedCustomer) {
      return `${selectedCustomer.user.firstName} ${selectedCustomer.user.lastName} (${selectedCustomer.user.email})`;
    } else {
      // If we have a value but no customer data, show a loading state
      return "Loading customer...";
    }
  };

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <FormControl>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={disabled}
            >
              {getSelectedCustomerText()}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput
              placeholder="Filter customers..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandEmpty>
              {isLoading && page === 1 ? "Loading..." : "No customers found."}
            </CommandEmpty>
            <CommandGroup
              className="max-h-[300px] overflow-y-auto"
              ref={listRef}
              onScroll={handleScroll}
            >
              {allCustomers.map((customer) => (
                <CommandItem
                  key={customer.id}
                  value={`${customer.user.firstName} ${customer.user.lastName} ${customer.user.email}`}
                  onSelect={() => {
                    onValueChange(customer.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      customer.id === value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span>
                      {customer.user.firstName} {customer.user.lastName}
                      {customer.businessAccount && (
                        <span className="ml-2 text-sm text-gray-500">
                          ({customer.businessAccount.companyName})
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">
                      {customer.user.email}
                    </span>
                  </div>
                </CommandItem>
              ))}
              {isLoadingMore && (
                <div className="py-2 text-center text-sm text-gray-500">
                  Loading more...
                </div>
              )}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// Form-specific wrapper that integrates with react-hook-form
interface FormCustomerSearchProps {
  name: string;
  label?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onCustomerSelect?: (customer: Customer | undefined) => void;
}

export function FormCustomerSearch({
  name,
  label = "Customer",
  placeholder,
  className,
  disabled,
  onCustomerSelect,
}: FormCustomerSearchProps) {
  const form = useFormContext();

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className="flex flex-col">
          <FormLabel>{label}</FormLabel>
          <CustomerSearch
            value={field.value as string}
            onValueChange={(value) => field.onChange(value)}
            onCustomerSelect={onCustomerSelect}
            placeholder={placeholder}
            className={className}
            disabled={disabled}
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
